/**
 * Prompts OCI reviewers for the optional sync-date boundary of a CSV export.
 *
 * This dialog owns only temporary form and progress state. The dashboard keeps
 * ownership of its active filters, detail loading, and file generation so the
 * prompt cannot define a second export path.
 */
import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui'

type ExportRange = 'all' | 'from-date'

export function OciCsvDownloadDialog({
  disabled,
  onDownload,
}: {
  disabled: boolean
  onDownload: (fromDate: string | null) => Promise<boolean>
}) {
  const [open, setOpen] = useState(false)
  const [range, setRange] = useState<ExportRange>('all')
  const [fromDate, setFromDate] = useState('')
  const [exporting, setExporting] = useState(false)

  const close = () => {
    setOpen(false)
    setRange('all')
    setFromDate('')
  }
  const download = async () => {
    setExporting(true)
    try {
      if (await onDownload(range === 'from-date' ? fromDate : null)) close()
    } finally {
      setExporting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={next => {
      if (exporting) return
      if (next) setOpen(true)
      else close()
    }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="oci-records-csv-download"
          disabled={disabled}
        >
          <Download className="mr-2 h-4 w-4" /> Download Awards CSV
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="oci-csv-download-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Download awards CSV</DialogTitle>
          <DialogDescription>
            Export the awards in the current dashboard filter. You can limit the file by sync date.
          </DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-3 py-2">
          <legend className="mb-2 text-sm font-medium">Awards to include</legend>
          <RadioGroup
            value={range}
            onValueChange={value => setRange(value as ExportRange)}
            disabled={exporting}
            className="gap-2"
          >
            <label className={`flex cursor-pointer gap-3 rounded-md border p-3 ${
              range === 'all' ? 'border-primary bg-muted/40' : ''
            }`}>
              <RadioGroupItem value="all" data-testid="oci-csv-all-awards" />
              <span>
                <span className="block text-sm font-medium">All awards</span>
                <span className="block text-xs text-muted-foreground">
                  Include every award in the current dashboard filter.
                </span>
              </span>
            </label>
            <label className={`flex cursor-pointer gap-3 rounded-md border p-3 ${
              range === 'from-date' ? 'border-primary bg-muted/40' : ''
            }`}>
              <RadioGroupItem value="from-date" data-testid="oci-csv-on-or-after" />
              <span>
                <span className="block text-sm font-medium">On or after</span>
                <span className="block text-xs text-muted-foreground">
                  Include awards synced on or after a selected date.
                </span>
              </span>
            </label>
          </RadioGroup>
          {range === 'from-date' && (
            <div className="space-y-2 pl-8">
              <Label htmlFor="oci-csv-from-date">Sync date</Label>
              <Input
                id="oci-csv-from-date"
                data-testid="oci-csv-from-date"
                type="date"
                value={fromDate}
                onChange={event => setFromDate(event.target.value)}
                disabled={exporting}
              />
            </div>
          )}
        </fieldset>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={exporting}>Cancel</Button>
          <Button
            data-testid="oci-csv-confirm-download"
            onClick={() => void download()}
            disabled={exporting || (range === 'from-date' && !fromDate)}
          >
            {exporting
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing findings…</>
              : 'Download CSV'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
